import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, expand, map, Observable, reduce } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { HttpGetOptions, HttpPaginationParams, HttpResponse } from '@isaia/entity/http';
import { PRICE_API_ORIGIN } from './price-api-origin.token';
import { createPrice, PriceKeyed, PriceXhr } from '@isaia/entity/price';

type AvailableFilters = 'priceListId' | 'categoryPriceCode' | 'pricingGroup' | 'modelGroup' | 'season';
type QueryParams = Partial<Record<`filter[${AvailableFilters}]`, string>>;
export type HttpGetPricesOptions = HttpGetOptions<HttpPaginationParams & QueryParams>;

/**
 * Rows requested per page. GET /prices returns only `{ data }` (no pagination
 * block / reliable total), and the API reads `page[offset]` as a PAGE INDEX
 * (first row = offset * limit), not a row offset. So we request page 0, 1, 2…
 * until a page comes back with fewer rows than the limit.
 */
export const PRICE_PAGE_LIMIT = 20000;

/** Safety cap on pages per fetch (20 × 20,000 = 400,000 rows). */
export const PRICE_MAX_PAGES = 20;

@Injectable()
export class PriceApiService {
  private readonly http = inject(HttpClient);
  private readonly API_ORIGIN = inject(PRICE_API_ORIGIN);

  public getAllPriceList(options?: HttpGetPricesOptions) {
    return this.getAllPages(options).pipe(map((prices) => toPriceKeyed(prices)));
  }

  public getPriceList(priceListId: string, options?: HttpGetPricesOptions) {
    return this.getAllPages({ ...options, params: { ...options?.params, 'filter[priceListId]': priceListId } }).pipe(
      map((prices) => toPriceKeyed(prices)),
    );
  }

  /**
   * Fetches every page of /prices for the given filters. A caller-supplied
   * `page[limit]` sets the page size and `page[offset]` the first page index.
   */
  private getAllPages(options?: HttpGetPricesOptions): Observable<PriceXhr[]> {
    const url = `${this.API_ORIGIN}/prices`;
    const limit = positiveInt(options?.params?.['page[limit]']) || PRICE_PAGE_LIMIT;
    const firstPage = positiveInt(options?.params?.['page[offset]']);

    const fetchPage = (page: number) =>
      this.http
        .get<HttpResponse<PriceXhr[]>>(url, {
          ...options,
          params: { ...options?.params, 'page[limit]': limit, 'page[offset]': page },
        })
        .pipe(map((response) => ({ page, rows: response?.data ?? [] })));

    return fetchPage(firstPage).pipe(
      expand(({ page, rows }) => {
        if (rows.length < limit) {
          return EMPTY;
        }
        if (page - firstPage + 1 >= PRICE_MAX_PAGES) {
          const message =
            `[PriceApiService] Stopped after ${PRICE_MAX_PAGES} pages of ${limit} prices ` +
            `(${JSON.stringify(options?.params ?? {})}); remaining prices were not loaded.`;
          console.warn(message);
          Sentry.captureMessage(message, 'warning');
          return EMPTY;
        }
        return fetchPage(page + 1);
      }),
      reduce<{ rows: PriceXhr[] }, PriceXhr[]>((all, { rows }) => {
        all.push(...rows);
        return all;
      }, []),
    );
  }
}

function toPriceKeyed(prices: PriceXhr[]): PriceKeyed {
  return prices.reduce<PriceKeyed>((acc, priceXhr) => {
    if (!acc[priceXhr.priceListId]) {
      acc[priceXhr.priceListId] = [];
    }
    acc[priceXhr.priceListId].push(createPrice(priceXhr));
    return acc;
  }, {});
}

function positiveInt(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 0;
}
